import React, { useState, useRef } from 'react';
import { Card, Spin, Alert, message, Button, Typography, Space, Upload } from 'antd';
import FormikDynamicForm from './FormikDynamicForm';
import { useFormSchema, useFormSubmission } from '../../hooks/useFormSchema';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EditOutlined, CameraOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../utils/config';
import dayjs from 'dayjs';

const FormikForm = ({
  formId,
  uniqueValue = null,
  uniqueField = null,
  tableName = null,
  onSuccess = null,
  onError = null,
  initialValues = {},
  extraData = {}
}) => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { schema, loading, error } = useFormSchema(formId, uniqueValue, uniqueField, tableName, extraData);
  const { submitForm, submitting, error: submitError } = useFormSubmission();
  const [parsingImage, setParsingImage] = useState(false);
  const formRef = useRef(null);

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      let processedValues = { ...values };
      if (formId === 199 || formId === '199') {
        if (processedValues['banka-pass'] !== undefined && processedValues['banka-pass'] !== null && processedValues['banka-pass'] !== '') {
          processedValues['banka'] = processedValues['banka-pass'];
        }

        const bankaPassValue = processedValues['banka-pass'];
        if (bankaPassValue !== undefined && bankaPassValue !== null && bankaPassValue !== '') {
          const selectedKey = Array.isArray(bankaPassValue) ? String(bankaPassValue[0]) : String(bankaPassValue);
          const teklifFieldName = `teklif[${selectedKey}]`;
          let teklifValue = null;

          if (processedValues[teklifFieldName] !== undefined) {
            teklifValue = processedValues[teklifFieldName];
          }
          else if (Array.isArray(processedValues['teklif']) && processedValues['teklif'][parseInt(selectedKey)] !== undefined) {
            teklifValue = processedValues['teklif'][parseInt(selectedKey)];
          }

          const isValidValue = (val) => {
            if (val === undefined || val === null || val === '') {
              return false;
            }
            if (typeof val === 'string' && val.trim() === '') {
              return false;
            }
            if (typeof val === 'number' && isNaN(val)) {
              return false;
            }
            return true;
          };

          if (isValidValue(teklifValue)) {
            const numericValue = typeof teklifValue === 'string' ? parseFloat(teklifValue) : teklifValue;
            if (!isNaN(numericValue) && isFinite(numericValue)) {
              processedValues['teklif'] = numericValue;
            } else {
              processedValues['teklif'] = teklifValue;
            }
          } else {
            if (Array.isArray(processedValues['teklif'])) {
              for (let i = 0; i < processedValues['teklif'].length; i++) {
                const arrayValue = processedValues['teklif'][i];
                if (isValidValue(arrayValue)) {
                  const numericValue = typeof arrayValue === 'string' ? parseFloat(arrayValue) : arrayValue;
                  if (!isNaN(numericValue) && isFinite(numericValue)) {
                    processedValues['teklif'] = numericValue;
                    break;
                  }
                }
              }
            }

            if (Array.isArray(processedValues['teklif'])) {
              const allTeklifFields = Object.keys(processedValues).filter(k => k.startsWith('teklif[') && k.endsWith(']'));
              for (const teklifField of allTeklifFields) {
                const fallbackValue = processedValues[teklifField];
                if (isValidValue(fallbackValue)) {
                  const numericValue = typeof fallbackValue === 'string' ? parseFloat(fallbackValue) : fallbackValue;
                  if (!isNaN(numericValue) && isFinite(numericValue)) {
                    processedValues['teklif'] = numericValue;
                    break;
                  }
                }
              }
            }

            if (Array.isArray(processedValues['teklif'])) {
              delete processedValues['teklif'];
            }
          }

          Object.keys(processedValues).forEach(key => {
            if (key.startsWith('teklif[') && key.endsWith(']')) {
              delete processedValues[key];
            }
          });
        }

        delete processedValues['banka-pass'];
      }

      const userId = extraData?.user_id ||
        extraData?.kullanici_no ||
        user?.kullanici_no ||
        null;
      const result = await submitForm(processedValues, schema, uniqueField, uniqueValue, extraData, userId);

      if (result.success) {
        if (onSuccess) {
          onSuccess(result);
        }

        if (schema.redirect) {
          if (schema.redirect.startsWith('/detay/')) {
            navigate(schema.redirect);
          } else if (schema.redirect.startsWith('/')) {
            navigate(schema.redirect);
          } else {
            window.location.href = schema.redirect;
          }
        }
      } else {
        setStatus({ error: result.message || 'Form gönderilirken hata oluştu' });
        message.error(result.message || 'Form gönderilirken hata oluştu');
      }
    } catch (err) {
      console.error('FormikForm handleSubmit hatası:', err);
      setStatus({ error: err.message || 'Form gönderilirken hata oluştu' });
      message.error(err.message || 'Form gönderilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleValuesChange = (values) => {
    console.log('Form values changed:', values);
  };

  const handleImageUpload = async (file) => {
    if (!formId) {
      message.error('Form ID bulunamadı');
      return false;
    }

    setParsingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/form/formlar/${formId}/parse-image`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    const result = await response.json();

    if (result.success && result.data) {
      if (formRef.current && formRef.current.setFieldValue) {
        const parsedData = result.data;
        let filledCount = 0;

        const dateFields = new Set();
        if (schema && schema.fields) {
          schema.fields.forEach(field => {
            if (field.type === 'date' || field.type === 9) {
              dateFields.add(field.name);
            }
          });
        }

        for (const [fieldName, fieldValue] of Object.entries(parsedData)) {
          if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
            let finalValue = fieldValue;

            if (dateFields.has(fieldName)) {
              const dateStr = String(fieldValue).trim();
              if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                finalValue = dateStr;
              } else {
                let parsedDate = null;
                if (/^\d{2}[/.]\d{2}[/.]\d{4}$/.test(dateStr)) {
                  parsedDate = dayjs(dateStr.replace('.', '/'), 'DD/MM/YYYY');
                }
                else if (/^\d{4}[/.]\d{2}[/.]\d{2}$/.test(dateStr)) {
                  parsedDate = dayjs(dateStr.replace('.', '/'), 'YYYY/MM/DD');
                }
                else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
                  parsedDate = dayjs(dateStr, 'DD-MM-YYYY');
                }
                else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
                  parsedDate = dayjs(dateStr, 'MM/DD/YYYY');
                }
                else {
                  parsedDate = dayjs(dateStr);
                }

                if (parsedDate && parsedDate.isValid()) {
                  finalValue = parsedDate.format('YYYY-MM-DD');
                }
              }
            }

            formRef.current.setFieldValue(fieldName, finalValue);
            filledCount++;
          }
        }

        message.success(`${filledCount} alan başarıyla dolduruldu`);
      } else {
        message.warning('Form referansı bulunamadı, veriler yüklenemedi');
      }
    } else {
      message.error('Image parse edilemedi');
    }
    return false;
  };

  const mergedInitialValues = {
    ...initialValues,
    ...extraData
  };

  if (loading) {
    return (
      <Card
        style={{
          background: darkMode ? '#1b1d22' : '#fff',
          border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
        }}
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{
            marginTop: 16,
            color: darkMode ? '#fff' : '#000'
          }}>
            Form yükleniyor...
          </p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        style={{
          background: darkMode ? '#1b1d22' : '#fff',
          border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
        }}
      >
        <Alert
          message="Form Yükleme Hatası"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (!schema) {
    return (
      <Card
        style={{
          background: darkMode ? '#1b1d22' : '#fff',
          border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
        }}
      >
        <Alert
          message="Form Bulunamadı"
          description="Belirtilen form ID'si bulunamadı."
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space size={8}>
          <Typography.Text style={{ color: darkMode ? '#fff' : '#000' }}>
            {schema.formName}
          </Typography.Text>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/form_manager/${schema.formId}`)}
          />
          <Upload
            beforeUpload={handleImageUpload}
            showUploadList={false}
            accept="image/*"
            disabled={parsingImage}
          >
            <Button
              size="small"
              icon={<CameraOutlined />}
              loading={parsingImage}
              title="Resimden veri çıkar"
            >
              {parsingImage ? 'İşleniyor...' : '📷'}
            </Button>
          </Upload>
        </Space>
      }
      style={{
        background: darkMode ? '#1b1d22' : '#fff',
        border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
      }}
    >
      <FormikDynamicForm
        ref={formRef}
        schema={schema}
        initialValues={mergedInitialValues}
        onSubmit={handleSubmit}
        onValuesChange={handleValuesChange}
        loading={submitting}
        submitButtonText={schema.submitText}
        darkMode={darkMode}
        formId={formId}
        uniqueValue={uniqueValue}
      />

      {submitError && (
        <Alert
          message="Gönderim Hatası"
          description={submitError}
          type="error"
          style={{ marginTop: 16 }}
          showIcon
        />
      )}
    </Card>
  );
};

export default FormikForm;
